import { useState } from 'react';
import { Calendar, Plus, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';

const Scheduling = () => {
  const { isDark } = useTheme();
  const [promotionWeeks] = useState([
    {
      id: 'PW-001',
      week: 'Week 1, Dec 2025',
      startDate: '01/12/2025',
      endDate: '07/12/2025',
      featuredProjects: ['EcoTrack', 'SkillSwap'],
      status: 'Scheduled',
      problemStatement: 'PS-005',
    },
    {
      id: 'PW-002',
      week: 'Week 2, Dec 2025',
      startDate: '08/12/2025',
      endDate: '14/12/2025',
      featuredProjects: ['LocalVoice'],
      status: 'Draft',
      problemStatement: 'PS-006',
    },
    {
      id: 'PW-003',
      week: 'Week 3, Nov 2025',
      startDate: '15/11/2025',
      endDate: '21/11/2025',
      featuredProjects: ['MediRemind', 'FoodBridge'],
      status: 'Active',
      problemStatement: 'PS-003',
    },
  ]);

  const handleCreateSchedule = () => {
    // TODO: Implement create schedule
    console.log('Creating new promotion week...');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Scheduling</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Schedule promotion weeks and manage featured projects</p>
        </div>
        <Button 
          onClick={handleCreateSchedule} 
          className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Promotion Week
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Weeks</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{promotionWeeks.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
          <p className="text-2xl font-bold text-green-600">
            {promotionWeeks.filter(pw => pw.status === 'Active').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Scheduled</p>
          <p className="text-2xl font-bold text-blue-600">
            {promotionWeeks.filter(pw => pw.status === 'Scheduled').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Draft</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {promotionWeeks.filter(pw => pw.status === 'Draft').length}
          </p>
        </div>
      </div>

      {/* Promotion Weeks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {promotionWeeks.map((week) => (
          <div
            key={week.id}
            className={`rounded-xl p-6 shadow-md border transition-colors ${
              isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20 hover:border-purple-500/40' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className={`w-5 h-5 transition-colors ${
                  isDark ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <h3 className={`font-semibold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{week.week}</h3>
              </div>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  week.status === 'Active'
                    ? isDark
                      ? 'bg-green-500/30 text-green-200'
                      : 'bg-green-100 text-green-700'
                    : week.status === 'Scheduled'
                    ? isDark
                      ? 'bg-blue-500/30 text-blue-200'
                      : 'bg-blue-100 text-blue-700'
                    : isDark
                      ? 'bg-gray-500/30 text-gray-300'
                      : 'bg-gray-100 text-gray-700'
                }`}
              >
                {week.status}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div className={`flex items-center gap-2 text-sm transition-colors ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <Clock className="w-4 h-4" />
                <span>
                  {week.startDate} - {week.endDate}
                </span>
              </div>
              <div className="text-sm">
                <p className={`mb-1 transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>Problem Statement:</p>
                <p className={`font-medium transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{week.problemStatement}</p>
              </div>
              <div className="text-sm">
                <p className={`mb-1 transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>Featured Projects:</p>
                <div className="flex flex-wrap gap-1">
                  {week.featuredProjects.map((project, idx) => (
                    <span
                      key={idx}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        isDark
                          ? 'bg-purple-500/30 text-purple-200'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {project}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className={`flex-1 ${
                  isDark 
                    ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Edit
              </Button>
              {week.status === 'Draft' && (
                <Button 
                  size="sm" 
                  className={`flex-1 ${
                    isDark
                      ? 'bg-green-500/30 text-green-200 hover:bg-green-500/40 hover:text-white border border-green-500/30'
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Publish
                </Button>
              )}
              {week.status === 'Active' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`flex-1 ${
                    isDark
                      ? 'border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300'
                      : 'text-red-600 border-red-300 hover:bg-red-50'
                  }`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  End
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Scheduling;

